/**
 * Zenprexi Bio-Risk™ Dashboard Slice
 */

import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { fleetApi } from '@/services/api';
import { logout } from '@/store/authSlice';
import type { FleetSummary, FleetWorkerStatus, Alert, Department } from '@/types';

interface DashboardState {
  summary: FleetSummary | null;
  workers: FleetWorkerStatus[];
  alerts: Alert[];
  departments: Department[];
  selectedDepartment: string | null;
  isLoading: boolean;
  lastUpdated: string | null;
  error: string | null;
}

const initialState: DashboardState = {
  summary: null,
  workers: [],
  alerts: [],
  departments: [],
  selectedDepartment: null,
  isLoading: false,
  lastUpdated: null,
  error: null,
};

// Async thunks
export const fetchDashboard = createAsyncThunk(
  'dashboard/fetchDashboard',
  async (_, { rejectWithValue }) => {
    try {
      return await fleetApi.getDashboard();
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      return rejectWithValue(err.response?.data?.message || 'Failed to fetch dashboard');
    }
  }
);

export const fetchSummary = createAsyncThunk(
  'dashboard/fetchSummary',
  async (_, { rejectWithValue }) => {
    try {
      const response = await fleetApi.getSummary();
      return response as FleetSummary;
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      return rejectWithValue(err.response?.data?.message || 'Failed to fetch summary');
    }
  }
);

export const fetchWorkers = createAsyncThunk(
  'dashboard/fetchWorkers',
  async (params: { onShiftOnly?: boolean; classification?: string; department?: string } = {}, { rejectWithValue }) => {
    try {
      return (await fleetApi.getWorkers(params)) as FleetWorkerStatus[];
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      return rejectWithValue(err.response?.data?.message || 'Failed to fetch workers');
    }
  }
);

export const fetchAlerts = createAsyncThunk(
  'dashboard/fetchAlerts',
  async (params: { acknowledged?: boolean; limit?: number } = {}, { rejectWithValue }) => {
    try {
      // If acknowledged is not specified, fetch both active and acknowledged alerts
      if (params.acknowledged === undefined) {
        const [activeAlerts, acknowledgedAlerts] = await Promise.all([
          fleetApi.getAlerts({ ...params, acknowledged: false }),
          fleetApi.getAlerts({ ...params, acknowledged: true }),
        ]);
        return [...activeAlerts, ...acknowledgedAlerts] as Alert[];
      }
      
      const response = await fleetApi.getAlerts(params);
      return response as Alert[];
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      return rejectWithValue(err.response?.data?.message || 'Failed to fetch alerts');
    }
  }
);

export const fetchDepartments = createAsyncThunk(
  'dashboard/fetchDepartments',
  async (_, { rejectWithValue }) => {
    try {
      const response = await fleetApi.getDepartments();
      return response as Department[];
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      return rejectWithValue(err.response?.data?.message || 'Failed to fetch departments');
    }
  }
);

export const acknowledgeAlert = createAsyncThunk(
  'dashboard/acknowledgeAlert',
  async (alertId: string, { rejectWithValue }) => {
    try {
      await fleetApi.acknowledgeAlert(alertId);
      return alertId;
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      return rejectWithValue(err.response?.data?.message || 'Failed to acknowledge alert');
    }
  }
);

// Slice
const dashboardSlice = createSlice({
  name: 'dashboard',
  initialState,
  reducers: {
    setSelectedDepartment: (state, action: PayloadAction<string | null>) => {
      state.selectedDepartment = action.payload;
    },
    updateWorkerCari: (state, action: PayloadAction<{ workerId: string; score: number; classification: string }>) => {
      const worker = state.workers.find(w => w.workerId === action.payload.workerId);
      if (worker) {
        worker.currentCari = action.payload.score;
        worker.classification = action.payload.classification as FleetWorkerStatus['classification'];
        worker.lastReadingAt = new Date().toISOString();
      }
    },
    addAlert: (state, action: PayloadAction<Alert>) => {
      state.alerts.unshift(action.payload);
      if (state.summary) {
        state.summary.activeAlerts += 1;
      }
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Dashboard
      .addCase(fetchDashboard.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchDashboard.fulfilled, (state, action) => {
        state.isLoading = false;
        state.summary = action.payload.summary;
        state.workers = action.payload.workers ?? [];
        state.alerts = action.payload.recentAlerts ?? [];
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(fetchDashboard.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Fetch Summary
      .addCase(fetchSummary.fulfilled, (state, action) => {
        state.summary = action.payload;
        state.lastUpdated = new Date().toISOString();
      })
      // Fetch Workers
      .addCase(fetchWorkers.fulfilled, (state, action) => {
        state.workers = action.payload;
      })
      // Fetch Alerts
      .addCase(fetchAlerts.fulfilled, (state, action) => {
        state.alerts = action.payload;
      })
      // Fetch Departments
      .addCase(fetchDepartments.fulfilled, (state, action) => {
        state.departments = action.payload;
      })
      // Acknowledge Alert
      .addCase(acknowledgeAlert.fulfilled, (state, action) => {
        const alert = state.alerts.find(a => a.id === action.payload);
        if (alert) {
          alert.acknowledged = true;
          alert.acknowledgedAt = new Date().toISOString();
        }
        if (state.summary && state.summary.activeAlerts > 0) {
          state.summary.activeAlerts -= 1;
        }
      })
      .addCase(logout, () => initialState);
  },
});

export const { setSelectedDepartment, updateWorkerCari, addAlert, clearError } = dashboardSlice.actions;
export default dashboardSlice.reducer;
